import {useMemo} from 'react'
import {useNavigate} from 'react-router-dom'
import {useI18n} from '@/i18n'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {Badge} from '@/components/ui/badge'
import {TableEmptyState} from '@/components/shared/TableEmptyState'
import {Pagination} from '@/components/ui/pagination'
import {SortableHeader} from '@/components/ui/sortable-header'
import {useServerPagination, SortDir} from '@/hooks/use-server-pagination'
import {NotificationControllerListEndpoint} from '@/types/endpoint-contracts'
import {formatTimestamp} from '@/lib/date-utils'
import {routes} from '@/lib/routes'
import {Bell, Circle} from 'lucide-react'
import {Icon} from '@/components/shared/Icon'
import {TruncatedCell} from '@/components/shared/TruncatedCell'
import {PageLayout} from '@/components/shared/PageLayout'
import type {Notification as IAMNotification} from '@/types/notification'
import {useNotifications} from './useNotifications'
import {getEventTypeLabel, getEventTypeBadgeVariant} from './notificationUtils'

export function NotificationHistory() {
    const i18n = useI18n()
    const navigate = useNavigate()

    const {
        page, pageSize, sortBy, sortDir, queryString,
        setSort, setPage, setPageSize, pageSizeOptions,
        searchAndFilters, setSearchAndFilters,
    } = useServerPagination({
        endpoint: NotificationControllerListEndpoint,
        defaultSortBy: NotificationControllerListEndpoint.sort.CREATED_AT,
        defaultSortDir: SortDir.DESC,
        sortable: true,
    })

    const {data, isLoading, error} = useNotifications(queryString)

    const totalItems = data?.totalElements || 0
    const totalPages = data?.totalPages || 0
    const hasActiveFilters = useMemo(() => {
        return !!searchAndFilters.q
            || Object.keys(searchAndFilters.filters).length > 0
    }, [searchAndFilters])

    const handleRowClick = (notification: IAMNotification) => {
        navigate(routes.my.notifications.detail(notification.id))
    }

    const filterControls = (
        <UnifiedSearchBar
            contract={NotificationControllerListEndpoint}
            value={searchAndFilters}
            onChange={setSearchAndFilters}
            placeholder={i18n.notifications.filterPlaceholder as string}
            testIdNamespace="notifications"
        />
    )

    return (
        <PageLayout
            variant="list"
            icon={Bell}
            title={String(i18n.notifications.title)}
            description={String(i18n.notifications.subtitle)}
            filters={filterControls}
            error={error}
            loading={isLoading}
            count={totalItems}
            pagination={
                totalItems > 0 ? (
                    <Pagination
                        page={page}
                        pageSize={pageSize}
                        totalItems={totalItems}
                        totalPages={totalPages}
                        onPageChange={setPage}
                        onPageSizeChange={setPageSize}
                        pageSizeOptions={pageSizeOptions}
                    />
                ) : undefined
            }
            data-testid="notification-history-title"
        >
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead className="w-8" data-testid="col-read-indicator"/>
                        <TableHead className="text-center" data-testid="col-event-type">{i18n.notifications.eventType}</TableHead>
                        <TableHead data-testid="col-message">{i18n.notifications.message}</TableHead>
                        <TableHead data-testid="col-date">
                            <SortableHeader
                                label={i18n.notifications.date as string}
                                sortKey={NotificationControllerListEndpoint.sort.CREATED_AT}
                                currentSortBy={sortBy}
                                currentSortDir={sortDir}
                                onSort={setSort}
                            />
                        </TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {data?.content.length === 0 ? (
                        <TableEmptyState
                            colSpan={4}
                            hasFilters={!!hasActiveFilters}
                            matchMessage={i18n.notifications.noNotificationsMatch}
                            emptyMessage={i18n.notifications.noNotifications}
                        />
                    ) : (
                        data?.content.map((notification) => (
                            <TableRow
                                key={notification.id}
                                className="cursor-pointer hover:bg-muted/50"
                                onClick={() => handleRowClick(notification)}
                                data-testid={`notification-row-${notification.id}`}
                            >
                                <TableCell className="w-8 pr-0">
                                    {!notification.read && (
                                        <Icon icon={Circle} size="xs" className="text-primary fill-primary"/>
                                    )}
                                </TableCell>
                                <TableCell className="text-center">
                                    <Badge variant={getEventTypeBadgeVariant(notification.eventType)} data-testid={`notification-event-type-${notification.id}`}>
                                        {getEventTypeLabel(notification.eventType, i18n.notifications)}
                                    </Badge>
                                </TableCell>
                                <TruncatedCell maxWidth="520px" title={notification.message}>
                                    <span className={notification.read ? '' : 'font-medium'} data-testid={`notification-message-${notification.id}`}>
                                        {notification.message}
                                    </span>
                                </TruncatedCell>
                                <TableCell className="text-sm text-muted-foreground">
                                    {formatTimestamp(notification.createdAt)}
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </PageLayout>
    )
}
